import React from 'react';
import { Box, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery, Grid, Typography, Button } from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Note as NoteIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface MenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  debug?: boolean;
  debugColor?: string;
  debugLabel?: string;
}

interface BottomBarProps {
  menuItems?: MenuItem[];
  currentPage?: string;
  onPageChange?: (page: string) => void;
  isEmbedded?: boolean;
}

const BottomBar = React.forwardRef<HTMLDivElement, BottomBarProps>(({ 
  menuItems, 
  currentPage = 'manage', 
  onPageChange, 
  isEmbedded = false 
}, ref) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  // 根據螢幕尺寸決定初始狀態
  const getInitialScrollState = () => {
    const isDesktop = window.innerWidth > 800;
    const isPortrait = window.innerHeight >= 700;
    return !(isDesktop || isPortrait); // 桌面版和手機直式版初始顯示，手機橫式版初始隱藏
  };
  
  const [isScrolledUp, setIsScrolledUp] = React.useState(false); // 永久顯示
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // 🔧 修復：響應視窗大小變化
  const [windowDimensions, setWindowDimensions] = React.useState(() => {
    const initialDimensions = {
      width: window.innerWidth,
      height: window.innerHeight,
      isLandscape: window.innerWidth > window.innerHeight,
      isPortrait: window.innerWidth <= window.innerHeight,
      isSmallScreen: (window.innerWidth <= 800 && window.innerHeight < 700) || window.innerHeight < 500
    };
    
    return initialDimensions;
  });

  // 監聽視窗大小變化
  React.useEffect(() => {
    const handleResize = () => {
      const newDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
        isLandscape: window.innerWidth > window.innerHeight,
        isPortrait: window.innerWidth <= window.innerHeight,
        isSmallScreen: (window.innerWidth <= 800 && window.innerHeight < 700) || window.innerHeight < 500
      };
      
      setWindowDimensions(newDimensions);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isLandscape = windowDimensions.isLandscape;
  const isPortrait = windowDimensions.isPortrait;
  
  // 檢查當前是否在 ManagePages 或 NotesPage 頁面
  const [isManagePages, setIsManagePages] = React.useState(false);
  const [isNotesPage, setIsNotesPage] = React.useState(false);
  
  // 監聽路由變化，更新頁面狀態
  React.useEffect(() => {
    const checkCurrentPage = () => {
      const currentPath = window.location.pathname;
      const isMP = currentPath.includes('/manage') || currentPath.includes('/ManagePages');
      const isNP = currentPath.includes('/notes') || currentPath === '/notes';
      setIsManagePages(isMP);
      setIsNotesPage(isNP);
    };
    
    // 初始檢查
    checkCurrentPage();
    
    // 監聽路由變化
    const handleRouteChange = () => {
      checkCurrentPage();
    };
    
    // 監聽 popstate 事件（瀏覽器前進/後退）
    window.addEventListener('popstate', handleRouteChange, { passive: true });
    
    // 監聽 pushstate 和 replacestate 事件
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      handleRouteChange();
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      handleRouteChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // 處理搜尋按鈕點擊
  const handleSearchClick = () => {
    const isLoggedIn = !!authUser?.email;
    // 調試日誌已移除
    // 調試日誌已移除
    
    if (isEmbedded && onPageChange) {
      // 🔧 修復：嵌入式模式下，切換到 manage 頁面
      // 調試日誌已移除
      onPageChange('manage');
    } else {
      // 傳統路由模式下，使用原有邏輯
      if (isLoggedIn) {
        // 已登入用戶 → 前往瀏覽記錄分頁
        console.log('🎯 已登入用戶，前往瀏覽記錄分頁');
        navigate('/manage', { 
          state: { 
            currentSection: 'personalHistory', // 瀏覽記錄分頁
            expandedMain: ['home', 'myPoint'] // 確保 myPoint 主項目是展開的
          } 
        });
      } else {
        // 未登入用戶 → 前往社群分頁
        console.log('🎯 未登入用戶，前往熱門重點分頁');
        navigate('/manage', { 
          state: { 
            currentSection: 'mediaPoint', // 熱門重點分頁
            expandedMain: ['home', 'mediaPoint'] // 確保 mediaPoint 主項目是展開的
          } 
        });
      }
    }
    
    // 只在非嵌入式模式下執行滾動邏輯
    if (!isEmbedded) {
      // 導航後滑動到頁面頂端，讓用戶能看到搜尋欄
      setTimeout(() => {
        console.log('🔍 BottomBar 開始滾動檢查...');
        
        // 立即滾動到頂端
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        console.log('📜 BottomBar 基本滾動完成');
      }, 300); // 增加延遲時間，確保 ManagePages 完全載入
    }
    
    // 只在非嵌入式模式下執行額外的延遲滾動
    if (!isEmbedded) {
      // 額外的延遲滾動，確保在 ManagePages 渲染完成後執行
      setTimeout(() => {
      console.log('🔍 BottomBar 二次滾動檢查...');
      
      // 強制滾動所有可能的容器
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // 檢查所有元素
      const allElements = document.querySelectorAll('*');
      let scrollableCount = 0;
      
      allElements.forEach(element => {
        const computedStyle = window.getComputedStyle(element);
        const hasOverflow = computedStyle.overflow !== 'visible' || 
                           computedStyle.overflowY !== 'visible' || 
                           computedStyle.overflowX !== 'visible';
        
        if (hasOverflow) {
          element.scrollTop = 0;
          scrollableCount++;
        }
      });
      
      console.log(`📜 BottomBar 二次確認完成，重置了 ${scrollableCount} 個容器`);
      }, 800);
    }
  };

  // BottomBar 永久顯示，移除所有滾動監聽邏輯

  const items = menuItems ?? [];

  const drawerContent = (
    <Box sx={{ 
      width: '100%', 
      // 手機橫式佈局下調整內邊距，讓內容更緊湊
      p: windowDimensions.isSmallScreen ? 0.5 : 1, 
      overflowY: 'auto' 
    }}>
      <IconButton 
        onClick={handleDrawerToggle} 
        sx={{ 
          mb: windowDimensions.isSmallScreen ? 1 : 2,
          // 手機橫式佈局下調整關閉按鈕位置
          position: 'absolute',
          top: windowDimensions.isSmallScreen ? '8px' : '16px',
          right: windowDimensions.isSmallScreen ? '8px' : '16px',
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>
      <Grid container spacing={windowDimensions.isSmallScreen ? 0.25 : 0.5}>
        {items.map((item) => (
          <Grid item 
            xs={6}  // 手機直式模式：每行3個 (12/6=2, 但實際是3個一排)
            sm={4}  // 手機橫式模式：每行3個 (12/4=3)
            md={2}  // 桌面模式：每行6個 (12/2=6)
            key={item.key}
          >
            <Box
              onClick={() => {
                console.log('🔍 BottomBar 點擊項目:', item.key, item.label);
                console.log('🔍 onClick 函數類型:', typeof item.onClick);
                item.onClick();
                setDrawerOpen(false); // 立即關閉底部導航欄
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                // 手機橫式佈局下調整內邊距，讓內容更緊湊
                py: windowDimensions.isSmallScreen ? 1 : 1.5,
                borderRadius: 2,
                '&:active': { bgcolor: 'action.selected' },
                transition: 'background 0.2s',
                // 🔍 調試色塊
                ...(item.debug && {
                  backgroundColor: item.debugColor || '#FF0000',
                  border: '3px solid #FFFF00',
                  boxShadow: '0 0 20px rgba(255, 0, 0, 0.8)',
                  '&:hover': {
                    backgroundColor: item.debugColor || '#FF0000',
                    transform: 'scale(1.1)'
                  }
                })
              }}
            >
              <Box sx={{ 
                fontSize: windowDimensions.isSmallScreen ? 32 : 40, 
                color: 'primary.main', 
                mb: windowDimensions.isSmallScreen ? 0.125 : 0.25 
              }}>
                {item.icon}
              </Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: windowDimensions.isSmallScreen ? 11 : 14, // 🔍 調試時稍小一點以容納更多文字
                  textAlign: 'center', 
                  color: item.debug ? '#FFFFFF' : 'text.secondary', // 🔍 調試時白色文字
                  lineHeight: 1.2,
                  letterSpacing: '0.5px',
                  fontWeight: item.debug ? 'bold' : 'normal', // 🔍 調試時加粗
                }}
              >
                {item.debug ? (item.debugLabel || '🔴 ' + item.label + ' 🔴') : item.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // 🔧 移除調試日誌，避免頻繁輸出
  // console.log('🔍 BottomBar 渲染中...', { 
  //   isScrolledUp, 
  //   isManagePages, 
  //   isNotesPage, 
  //   currentPath: window.location.pathname,
  //   menuItemsLength: menuItems?.length || 0
  // });

  return (
    <>
      <Box
        ref={ref}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100vw', // 使用視窗寬度
          maxWidth: '100%', // 限制最大寬度
          bgcolor: '#f8f9fa', // 改為非常淺的淺灰色背景
          borderTop: '2px solid #e0e0e0', // 保持淺灰色邊框
          borderTopLeftRadius: 0, // 左上角改為方形
          borderTopRightRadius: 0, // 右上角改為方形
          display: 'flex !important', // 強制顯示
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box', // 確保邊框不會影響寬度
          overflow: 'hidden', // 防止內容溢出
          visibility: 'visible !important', // 強制可見
          opacity: '1 !important', // 強制不透明
          // 手機橫式佈局下進一步縮小高度，擴大播放內容區域
          height: windowDimensions.isSmallScreen 
            ? '40px'  // 手機橫式：40px
            : { xs: '60px', sm: '80px' },  // 其他模式：手機版60px（從50px），平板版80px（從68px）
          p: windowDimensions.isSmallScreen
            ? 0.15  // 手機橫式：2.4px內邊距（進一步縮小）
            : { xs: 0.5, sm: 1 },  // 其他模式：手機版8px，平板版16px
          zIndex: 99999, // 使用極高的 z-index，確保在最上層
          // 永久顯示 BottomBar
          transform: 'translateY(0)',
          // transition: 'transform 0.3s ease-in-out', // 暫時移除動畫
          // 添加陰影效果，讓 BottomBar 更明顯（調整為更淡的陰影）
          boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <Box sx={{ 
          position: 'relative',
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          overflow: 'hidden' // 防止內容溢出
        }}>
          {/* 首頁按鈕 - 位置 12% */}
          <Button
            onClick={() => {
              // 調試日誌已移除
              // 🔧 性能優化：使用 requestAnimationFrame 避免阻塞 UI
              requestAnimationFrame(() => {
                if (isEmbedded && onPageChange) {
                  onPageChange('open'); // 🔧 修復：切換到 open 頁面而不是 manage
                } else {
                  // 🔧 修復：使用 navigate 而不是 window.location.href 來避免頁面刷新
                  navigate('/');
                }
              });
            }}
            sx={{
              position: 'absolute',
              left: '12%', // 恢復原始位置
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: windowDimensions.isSmallScreen ? 'row' : 'column', // 手機橫式時改為橫向排列
              alignItems: 'center',
              gap: windowDimensions.isSmallScreen ? 0.25 : 0, // 手機橫式時添加間距
              minWidth: 'auto',
              p: 0.5,
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#1976d2'
              }
            }}
          >
            <HomeIcon sx={{ 
              fontSize: { xs: '28px', sm: '36px' }, 
              mb: windowDimensions.isSmallScreen ? 0 : 0.25 // 手機橫式時移除底部間距
            }} />
            <Typography variant="caption" sx={{ 
              fontSize: { xs: '12px', sm: '14px' }, 
              fontWeight: 'bold', 
              letterSpacing: '0.5px',
              mb: windowDimensions.isSmallScreen ? 0 : 0 // 手機橫式時移除底部間距
            }}>
              首頁
            </Typography>
          </Button>

          {/* 筆記按鈕 - 位置 31% */}
          <Button
            onClick={() => {
              // 調試日誌已移除
              // 調試日誌已移除
              if (isEmbedded && onPageChange) {
                // 調試日誌已移除
                onPageChange('notes');
              } else {
                // 調試日誌已移除
                navigate('/notes');
              }
            }}
            sx={{
              position: 'absolute',
              left: '31%', // 恢復原始位置
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: windowDimensions.isSmallScreen ? 'row' : 'column', // 手機橫式時改為橫向排列
              alignItems: 'center',
              gap: windowDimensions.isSmallScreen ? 0.25 : 0, // 手機橫式時添加間距
              minWidth: 'auto',
              p: 0.5,
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#1976d2'
              }
            }}
          >
            <NoteIcon sx={{ 
              fontSize: { xs: '28px', sm: '36px' }, 
              mb: windowDimensions.isSmallScreen ? 0 : 0.25 // 手機橫式時移除底部間距
            }} />
            <Typography variant="caption" sx={{ 
              fontSize: { xs: '12px', sm: '14px' }, 
              fontWeight: 'bold', 
              letterSpacing: '0.5px',
              mb: windowDimensions.isSmallScreen ? 0 : 0 // 手機橫式時移除底部間距
            }}>
              筆記
            </Typography>
          </Button>

          {/* 中間的紅色球體圖標 - 位置 50% */}
          <IconButton
            color="primary"
            onClick={() => {
              // 調試日誌已移除
              if (isEmbedded && onPageChange) {
                onPageChange('main');
              } else {
                navigate('/main');
              }
            }}
            onMouseEnter={() => {/* console.log('🔴 3D 紅球鼠標懸停') */}}
            onMouseLeave={() => {/* console.log('🔴 3D 紅球鼠標離開') */}}
            sx={{ 
              position: 'absolute',
              left: '50%', // 恢復到正中央
              transform: 'translateX(-50%)',
              // 手機橫式佈局下縮小紅球大小80%
              scale: windowDimensions.isSmallScreen 
                ? 0.8  // 手機橫式：縮小80%
                : 1,   // 其他模式：保持原大小
              '&:hover': {
                scale: windowDimensions.isSmallScreen 
                  ? 0.85  // 手機橫式：懸停時稍微放大
                  : 1.05, // 其他模式：懸停時稍微放大
              },
              transition: 'transform 0.2s ease-in-out'
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'radial-gradient(circle at 30% 30%, #ffffff 0%, #ff6b6b 30%, #e53935 70%, #b71c1c 100%)',
                border: '2px solid #fff',
                boxShadow: '2px 2px 8px rgba(183, 28, 28, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  width: '12px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.6)',
                  transform: 'rotate(-20deg)'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '6px',
                  left: '10px',
                  width: '4px',
                  height: '3px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.8)',
                  transform: 'rotate(-20deg)'
                }
              }}
            />
          </IconButton>

          {/* 搜尋按鈕 - 位置 69% */}
          <Button
            onClick={handleSearchClick}
            sx={{
              position: 'absolute',
              left: '69%', // 恢復原始位置
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: windowDimensions.isSmallScreen ? 'row' : 'column', // 手機橫式時改為橫向排列
              alignItems: 'center',
              gap: windowDimensions.isSmallScreen ? 0.25 : 0, // 手機橫式時添加間距
              minWidth: 'auto',
              p: 0.5,
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#1976d2'
              }
            }}
          >
            <SearchIcon sx={{ 
              fontSize: { xs: '28px', sm: '36px' }, 
              mb: windowDimensions.isSmallScreen ? 0 : 0.25 // 手機橫式時移除底部間距
            }} />
            <Typography variant="caption" sx={{ 
              fontSize: { xs: '12px', sm: '14px' }, 
              fontWeight: 'bold', 
              letterSpacing: '0.5px',
              mb: windowDimensions.isSmallScreen ? 0 : 0 // 手機橫式時移除底部間距
            }}>
              搜尋
            </Typography>
          </Button>

          {/* 更多按鈕 - 位置 88% */}
          <Button
            onClick={handleDrawerToggle}
            sx={{
              position: 'absolute',
              left: '88%', // 恢復原始位置
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: windowDimensions.isSmallScreen ? 'row' : 'column', // 手機橫式時改為橫向排列
              alignItems: 'center',
              gap: windowDimensions.isSmallScreen ? 0.25 : 0, // 手機橫式時添加間距
              minWidth: 'auto',
              p: 0.5,
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#1976d2'
              }
            }}
          >
            <MoreVertIcon sx={{ 
              fontSize: { xs: '28px', sm: '36px' }, 
              mb: windowDimensions.isSmallScreen ? 0 : 0.25 // 手機橫式時移除底部間距
            }} />
            <Typography variant="caption" sx={{ 
              fontSize: { xs: '12px', sm: '14px' }, 
              fontWeight: 'bold', 
              letterSpacing: '0.5px',
              mb: windowDimensions.isSmallScreen ? 0 : 0 // 手機橫式時移除底部間距
            }}>
              更多
            </Typography>
          </Button>
        </Box>
      </Box>
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
                  sx={{
            '& .MuiDrawer-paper': {
              // 🔧 修復：調整高度，避免被 BottomBar 遮擋
              height: windowDimensions.isSmallScreen 
                ? '30vh !important'  // 手機橫式：30vh（增加高度）
                : '50vh !important', // 其他模式：50vh（從45vh增加）
              maxHeight: windowDimensions.isSmallScreen 
                ? '30vh !important'  // 手機橫式：30vh
                : '50vh !important', // 其他模式：50vh
              borderTopLeftRadius: 0, // 左上角改為方形
              borderTopRightRadius: 0, // 右上角改為方形
              overflowY: 'auto',
              // 🔧 修復：調整位置，確保不被 BottomBar 遮擋
              ...(windowDimensions.isSmallScreen ? {
                bottom: '40px', // 與BottomBar高度對齊
                maxHeight: 'calc(100vh - 40px)', // 確保不超過視窗高度
              } : {
                bottom: '80px', // 其他模式：為 BottomBar 留出空間
                maxHeight: 'calc(100vh - 80px)', // 確保不超過視窗高度
              })
            },
          }}
      >
        {drawerContent}
      </Drawer>
          </>
    );
  });
  
  export default BottomBar; 